// Die Seite hinter dem Link aus der Bestätigungsmail.
//
// Sie wird meist aus dem Mailprogramm geöffnet, oft auf einem anderen Gerät
// und ohne Anmeldung. Deshalb braucht sie keine Sitzung: der Link selbst ist
// der Nachweis, und er gilt nur einmal.
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Hinweis, Karte, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';
import { LogoKachel } from '../../firma/komponenten/Logo';

type Stand = 'laeuft' | 'fertig' | 'abgelaufen' | 'ungueltig';

export default function BestaetigungEinloesen() {
  const { t } = useSprache();
  const [parameter] = useSearchParams();
  const [stand, setStand] = useState<Stand>('laeuft');
  // React ruft Effekte im Entwicklungsmodus zweimal auf. Ohne diese Sperre
  // ginge der zweite Aufruf gegen einen bereits eingelösten Link.
  const schonVersucht = useRef(false);

  useEffect(() => {
    if (schonVersucht.current) return;
    schonVersucht.current = true;
    const token = parameter.get('token');
    if (!token) { setStand('ungueltig'); return; }
    void (async () => {
      try {
        await api('/auth/bestaetigen', { methode: 'POST', daten: { token } });
        setStand('fertig');
      } catch (e) {
        setStand(e instanceof ApiFehler && e.code === 'ABGELAUFEN' ? 'abgelaufen' : 'ungueltig');
      }
    })();
  }, [parameter]);

  return (
    <div
      style={{
        // Oben die Statusleiste, unten die Home-Leiste freihalten; im Browser
        // sind beide Werte 0 und es bleibt bei den 3rem von vorher.
        paddingTop: 'calc(3rem + var(--sicher-oben))',
        paddingBottom: 'calc(3rem + var(--sicher-unten))',
      }}
      className="flex min-h-full items-center justify-center bg-gradient-to-b from-slate-100 to-slate-200 px-4 dark:from-slate-950 dark:to-slate-900">
      <div className="w-full max-w-md text-center">
        <LogoKachel groesse="gross" klasse="mx-auto mb-4 shadow-lg shadow-marke-600/25" />
        <Karte>
          <div className="space-y-4 p-6">
            {stand === 'laeuft' && (
              <><Spinner /><p className="text-sm text-slate-600 dark:text-slate-300">{t('bestaetigung.pruefe')}</p></>
            )}

            {stand === 'fertig' && (
              <>
                <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">{t('bestaetigung.fertigTitel')}</h1>
                <Hinweis art="erfolg">{t('bestaetigung.fertigText')}</Hinweis>
                <Link to="/"><Knopf variante="primaer" className="w-full">{t('bestaetigung.zurApp')}</Knopf></Link>
              </>
            )}

            {stand === 'abgelaufen' && (
              <>
                <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">{t('bestaetigung.abgelaufenTitel')}</h1>
                <Hinweis art="warnung">{t('bestaetigung.abgelaufenText')}</Hinweis>
                <Link to="/"><Knopf variante="primaer" className="w-full">{t('bestaetigung.zurApp')}</Knopf></Link>
              </>
            )}

            {stand === 'ungueltig' && (
              <>
                <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">{t('bestaetigung.ungueltigTitel')}</h1>
                <Hinweis art="fehler">{t('bestaetigung.ungueltigText')}</Hinweis>
                <Link to="/"><Knopf className="w-full">{t('bestaetigung.zurApp')}</Knopf></Link>
              </>
            )}
          </div>
        </Karte>
      </div>
    </div>
  );
}
